<template>
  <div class="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 p-6">
    <div class="flex items-center justify-between">
      <div class="flex-1">
        <p class="text-sm font-medium text-secondary-600 dark:text-secondary-400">
          {{ title }}
        </p>
        <p class="mt-2 text-3xl font-bold text-secondary-900 dark:text-white">
          <span v-if="prefix">{{ prefix }}</span>{{ formattedValue }}<span v-if="suffix">{{ suffix }}</span>
        </p>
        <div v-if="change !== undefined && change !== null" class="mt-2 flex items-center text-sm">
          <span
            :class="[
              'flex items-center font-medium',
              change >= 0 ? 'text-success-600 dark:text-success-400' : 'text-danger-600 dark:text-danger-400'
            ]"
          >
            <component :is="trendIcon" class="w-4 h-4 mr-1" />
            {{ Math.abs(change) }}%
          </span>
          <span class="ml-2 text-secondary-600 dark:text-secondary-400">vs previous period</span>
        </div>
      </div>
      <div :class="iconBgColor" class="p-3 rounded-lg">
        <component :is="iconComponent" class="w-8 h-8" :class="iconColor" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import {
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/vue/24/outline';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  value: {
    type: [Number, String],
    required: true,
  },
  change: {
    type: Number,
    default: null,
  },
  icon: {
    type: String,
    required: true,
  },
  color: {
    type: String,
    default: 'blue',
    validator: (value) => ['blue', 'green', 'red', 'purple', 'yellow'].includes(value),
  },
  prefix: {
    type: String,
    default: '',
  },
  suffix: {
    type: String,
    default: '',
  },
});

const iconMap = {
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
};

const iconComponent = computed(() => iconMap[props.icon] || ChartBarIcon);

const trendIcon = computed(() => {
  return props.change >= 0 ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
});

const colorClasses = {
  blue: {
    icon: 'text-primary-600 dark:text-primary-400',
    iconBg: 'bg-primary-100 dark:bg-primary-900/30',
  },
  green: {
    icon: 'text-success-600 dark:text-success-400',
    iconBg: 'bg-success-100 dark:bg-success-900/30',
  },
  red: {
    icon: 'text-danger-600 dark:text-danger-400',
    iconBg: 'bg-danger-100 dark:bg-danger-900/30',
  },
  purple: {
    icon: 'text-purple-600 dark:text-purple-400',
    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
  },
  yellow: {
    icon: 'text-warning-600 dark:text-warning-400',
    iconBg: 'bg-warning-100 dark:bg-warning-900/30',
  },
};

const iconColor = computed(() => colorClasses[props.color]?.icon || colorClasses.blue.icon);
const iconBgColor = computed(() => colorClasses[props.color]?.iconBg || colorClasses.blue.iconBg);

const formattedValue = computed(() => {
  const val = props.value;
  if (typeof val === 'number') {
    // Format numbers with commas
    return val.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  }
  return val;
});
</script>
